// useForegroundDwell.ts — accumulates wall-clock time a screen is FOREGROUNDED
// (tab visible AND app active). Pauses while the tab is hidden or the app is
// backgrounded, so a reflection left open overnight doesn't count as engagement.
// Returns getSeconds() — read it when the user advances.
import React from 'react';
import { App as CapApp } from '@capacitor/app';

export function useForegroundDwell(): () => number {
  const accumMs = React.useRef(0);
  const segStart = React.useRef<number | null>(Date.now());

  React.useEffect(() => {
    const pause = () => {
      if (segStart.current != null) {
        accumMs.current += Date.now() - segStart.current;
        segStart.current = null;
      }
    };
    const resume = () => {
      if (segStart.current == null) segStart.current = Date.now();
    };
    const onVis = () => (document.hidden ? pause() : resume());
    document.addEventListener('visibilitychange', onVis);
    let remove: (() => void) | undefined;
    CapApp.addListener('appStateChange', ({ isActive }) => (isActive ? resume() : pause()))
      .then((h) => {
        remove = () => h.remove();
      })
      .catch(() => {
        /* not native */
      });
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      remove?.();
    };
  }, []);

  return () => (accumMs.current + (segStart.current != null ? Date.now() - segStart.current : 0)) / 1000;
}
